import { useMemo, useState, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CreditCard,
  HandCoins,
  KeyRound,
  MapPin,
  Pencil,
  Plus,
  Save,
  Star,
  Trash2,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import type {
  Customer,
  CustomerAddress,
  CustomerAddressType,
  CustomerContact,
  CustomerContactType,
  LedgerEntry,
  PaginatedResponse,
} from '@/lib/api/types';
import { useAtLeast } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { customerKey } from '@/features/customers/keys';
import { availableCredit, creditLimitText } from '@/features/customers/credit';
import {
  date,
  dateTime,
  humanize,
  money,
  percent,
  phone as phoneFmt,
} from '@/lib/format';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Checkbox, Field, Input, MoneyInput, Select, Textarea } from '@/ui/Field';
import { Pagination } from '@/ui/Filters';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';
import { balanceLabel, balanceTone } from '@/ui/balance';

const LEDGER_PAGE_SIZE = 25;

type AccountSearch = { page: number };

export const Route = createFileRoute('/_app/customers_/$id')({
  staticData: { crumb: 'Account' },
  validateSearch: (s: Record<string, unknown>): AccountSearch => ({
    page: Number(s.page) > 0 ? Math.floor(Number(s.page)) : 1,
  }),
  component: CustomerAccountScreen,
});

/* ── Screen ───────────────────────────────────────────────────────────────── */

function CustomerAccountScreen() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const isManager = useAtLeast('manager');
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const customerQuery = useQuery({
    queryKey: customerKey(id),
    queryFn: () => api.get<Customer>(`/customers/${id}`),
  });
  // The API sends the raw serial id (a number) even though the type says
  // string. Normalising here keeps every child's query key and URL identical
  // to the route param — otherwise a balance write would invalidate a key
  // nobody is watching and the screen would show a stale balance.
  const raw = customerQuery.data;
  const customer = useMemo(
    () => (raw ? { ...raw, id: String(raw.id) } : undefined),
    [raw]
  );

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      active
        ? api.put<Customer>(`/customers/${id}`, { active: true })
        : api.del<Customer>(`/customers/${id}`),
    onSuccess: (updated) => {
      setDeactivateOpen(false);
      void qc.invalidateQueries({ queryKey: customerKey(id) });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(updated.active ? 'Account reactivated.' : 'Account deactivated.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (customerQuery.isError) {
    return (
      <Page>
        <PageHeader eyebrow="Customers" title="Account" />
        <PageBody>
          <ErrorState
            title="Could not load this customer"
            message={errorMessage(customerQuery.error)}
            onRetry={() => void customerQuery.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  if (!customer) {
    return (
      <Page>
        <PageHeader eyebrow="Customers" title="Account" />
        <PageBody>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </PageBody>
      </Page>
    );
  }

  const available = availableCredit(customer);

  return (
    <Page>
      <PageHeader
        eyebrow="Customers"
        title={customer.name}
        subtitle={
          [
            customer.companyName,
            customer.accountNumber ? `#${customer.accountNumber}` : null,
            phoneFmt(customer.phone, ''),
          ]
            .filter(Boolean)
            .join(' · ') || 'No company, account number or phone on file'
        }
        actions={
          <>
            <Button
              asChild
              variant="ghost"
              size="sm"
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link
                to="/customers"
                search={{ q: '', sort: 'balance', order: 'desc', page: 1 }}
              >
                Customers
              </Link>
            </Button>
            {isManager && customer.active !== false ? (
              <Button asChild size="sm" icon={<HandCoins className="size-3.5" />}>
                <Link to="/account-credits" search={{ customerId: customer.id }}>
                  Allocate credits
                </Link>
              </Button>
            ) : null}
            {customer.active === false ? (
              <Button
                size="sm"
                loading={setActive.isPending}
                onClick={() => setActive.mutate(true)}
              >
                Reactivate
              </Button>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setDeactivateOpen(true)}>
                Deactivate
              </Button>
            )}
          </>
        }
      />
      <PageBody>
        <div className="flex flex-col gap-3">
          {customer.active === false ? (
            <div className="border-warn/30 bg-warn-muted text-ink rounded border px-3 py-2 text-xs">
              This account is deactivated. It no longer appears at the register; the
              balance and ledger below are unchanged.
            </div>
          ) : null}

          <StatRow>
            <Stat
              label="Balance"
              value={money(customer.balance)}
              sub={balanceLabel(customer.balance)}
              tone={
                customer.balance > 0
                  ? 'negative'
                  : customer.balance < 0
                    ? 'positive'
                    : undefined
              }
            />
            <Stat
              label="Credit limit"
              value={creditLimitText(customer)}
              sub={customer.hasCredit ? 'house account on' : 'house account off'}
            />
            <Stat
              label="Available credit"
              value={
                available === null
                  ? '—'
                  : available === 'unlimited'
                    ? 'Unlimited'
                    : money(available)
              }
              sub={
                available === 'unlimited'
                  ? 'no limit set'
                  : typeof available === 'number' && available < 0
                    ? 'over limit'
                    : 'limit minus balance'
              }
              tone={
                typeof available === 'number' && available < 0 ? 'negative' : undefined
              }
            />
            <Stat
              label="Discount"
              value={customer.pctDiscount ? percent(customer.pctDiscount, 0) : '—'}
              sub={customer.taxExempt ? 'tax exempt' : 'taxed'}
            />
          </StatRow>

          <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_25rem]">
            <LedgerPanel customer={customer} />
            <div className="flex min-w-0 flex-col gap-3">
              <ProfilePanel key={customer.id} customer={customer} />
              <SecurityPanel customer={customer} />
              <ContactsPanel customerId={id} />
              <AddressesPanel customerId={id} />
            </div>
          </div>
        </div>
      </PageBody>

      <ConfirmDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        title="Deactivate this account?"
        description="A soft delete — nothing is erased."
        confirmLabel="Deactivate"
        loading={setActive.isPending}
        consequences={[
          'Removes the account from register customer lookup',
          `Keeps the ${money(customer.balance)} balance and the full ledger`,
          'Reactivating from this page restores it exactly',
        ]}
        onConfirm={() => setActive.mutate(false)}
      />
    </Page>
  );
}

/* ── Ledger ───────────────────────────────────────────────────────────────────
   The running-balance record. Both write paths below move a real balance, so
   both confirm, neither updates optimistically, and both report the balance
   the SERVER came back with — never a locally computed one.                  */

function LedgerPanel({ customer }: { customer: Customer }) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [payOpen, setPayOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const ledger = useQuery({
    queryKey: customerKey(customer.id, 'ledger', search.page),
    queryFn: () =>
      api.get<PaginatedResponse<LedgerEntry>>(`/customers/${customer.id}/ledger`, {
        params: { page: search.page, pageSize: LEDGER_PAGE_SIZE },
      }),
    placeholderData: (prev) => prev,
  });

  const rows = ledger.data?.data ?? [];
  const total = ledger.data?.total ?? 0;

  return (
    <Panel
      title="Ledger"
      description="Every charge, payment and adjustment on this account"
      bleed
      actions={
        <>
          <Button
            variant="primary"
            size="xs"
            icon={<Wallet className="size-3.5" />}
            onClick={() => setPayOpen(true)}
          >
            Record payment
          </Button>
          <Button size="xs" onClick={() => setAdjustOpen(true)}>
            Adjustment
          </Button>
        </>
      }
      footer={
        <Pagination
          page={search.page}
          pageSize={LEDGER_PAGE_SIZE}
          total={total}
          onPageChange={(page) => void navigate({ search: () => ({ page }) })}
        />
      }
    >
      {ledger.isError ? (
        <ErrorState
          title="Could not load the ledger"
          message={errorMessage(ledger.error)}
          onRetry={() => void ledger.refetch()}
          className="m-3"
        />
      ) : ledger.isLoading ? (
        <div className="flex flex-col gap-2 p-3">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          title="No ledger entries"
          description="Charges arrive from the register; payments and adjustments are recorded here."
        />
      ) : (
        <>
          <div
            className="border-line bg-surface-sunken text-2xs text-ink-faint grid items-center gap-x-3 border-b px-3 font-medium tracking-wider uppercase"
            style={{
              gridTemplateColumns: '9rem 7rem minmax(0,1fr) 6.5rem 7rem',
              height: '1.75rem',
            }}
          >
            <div>When</div>
            <div>Type</div>
            <div>Reference</div>
            <div className="text-right">Amount</div>
            <div className="text-right">Balance</div>
          </div>
          {rows.map((entry) => (
            <div
              key={entry.id}
              className="border-line-soft grid items-center gap-x-3 border-b px-3 py-1.5 text-xs"
              style={{ gridTemplateColumns: '9rem 7rem minmax(0,1fr) 6.5rem 7rem' }}
            >
              <div className="text-ink-muted font-mono tabular-nums">
                {dateTime(entry.createdAt)}
              </div>
              <div>
                <Badge tone={ledgerTone(entry.entryType)}>
                  {humanize(entry.entryType)}
                </Badge>
              </div>
              <div className="min-w-0">
                <div className="text-ink-muted truncate">{entry.reference || '—'}</div>
                {entry.notes ? (
                  <div className="text-2xs text-ink-faint truncate">{entry.notes}</div>
                ) : null}
              </div>
              <div
                className={cn(
                  'text-right font-mono tabular-nums',
                  balanceTone(entry.amountCents)
                )}
              >
                {entry.amountCents > 0 ? '+' : ''}
                {money(entry.amountCents)}
              </div>
              <div className="text-ink text-right font-mono tabular-nums">
                {money(entry.runningBalanceCents)}
              </div>
            </div>
          ))}
        </>
      )}

      <PaymentDialog customer={customer} open={payOpen} onOpenChange={setPayOpen} />
      <AdjustmentDialog
        customer={customer}
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
      />
    </Panel>
  );
}

function ledgerTone(entryType: string) {
  if (entryType === 'payment') return 'positive' as const;
  if (entryType === 'credit_memo') return 'info' as const;
  if (entryType === 'charge') return 'neutral' as const;
  return 'warn' as const;
}

/** Shared invalidation for anything that moved the balance. */
function useBalanceInvalidator(customerId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: customerKey(customerId) });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };
}

function PaymentDialog({
  customer,
  open,
  onOpenChange,
}: {
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useBalanceInvalidator(customer.id);
  const [amount, setAmount] = useState<number | null>(null);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setAmount(null);
    setReference('');
    setNotes('');
    setConfirming(false);
  };

  const record = useMutation({
    mutationFn: () =>
      api.post<Customer>(`/customers/${customer.id}/payments`, {
        amount: amount ?? 0,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: (updated) => {
      invalidate();
      onOpenChange(false);
      reset();
      // The server's balance, not ours.
      toast.success(
        `Payment recorded. Balance is now ${money(updated.balance)} (${balanceLabel(updated.balance)}).`
      );
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  const valid = amount !== null && amount > 0;
  const projected = customer.balance - (amount ?? 0);

  return (
    <>
      <Dialog
        open={open && !confirming}
        onOpenChange={(next) => {
          if (!next) reset();
          onOpenChange(next);
        }}
        title="Record a payment"
        description={`Reduces the house-account balance for ${customer.name}.`}
        width="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!valid}
              onClick={() => setConfirming(true)}
            >
              Review
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Amount received" required hint="Must be more than zero.">
            <MoneyInput autoFocus value={amount} onValueChange={setAmount} />
          </Field>
          <Field label="Reference" hint="Cheque number, transfer id, receipt number.">
            <Input
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
            />
          </Field>
          <Field label="Notes">
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </Field>
          <div className="border-line bg-surface-sunken text-ink-muted rounded border p-2 font-mono text-xs tabular-nums">
            {money(customer.balance)} → {money(projected)}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && confirming}
        onOpenChange={(next) => {
          if (!next) setConfirming(false);
        }}
        title="Record this payment?"
        description="This posts to the ledger immediately and cannot be undone — a mistake is corrected with an adjustment."
        confirmLabel="Record payment"
        variant="primary"
        loading={record.isPending}
        consequences={[
          `Applies ${money(amount ?? 0)} against the account`,
          `Balance ${money(customer.balance)} → ${money(projected)}`,
          reference.trim() ? `Reference: ${reference.trim()}` : 'No reference recorded',
        ]}
        onConfirm={() => record.mutate()}
      />
    </>
  );
}

type EntryType = 'adjustment' | 'credit_memo' | 'payment';

function AdjustmentDialog({
  customer,
  open,
  onOpenChange,
}: {
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useBalanceInvalidator(customer.id);
  const [entryType, setEntryType] = useState<EntryType>('adjustment');
  const [direction, setDirection] = useState<'charge' | 'credit'>('charge');
  const [amount, setAmount] = useState<number | null>(null);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);

  // payment and credit_memo may only ever REDUCE a balance (server rule).
  const forcedCredit = entryType !== 'adjustment';
  const effectiveDirection = forcedCredit ? 'credit' : direction;
  const amountCents =
    amount === null
      ? 0
      : effectiveDirection === 'credit'
        ? -Math.abs(amount)
        : Math.abs(amount);

  const reset = () => {
    setEntryType('adjustment');
    setDirection('charge');
    setAmount(null);
    setReference('');
    setNotes('');
    setConfirming(false);
  };

  const post = useMutation({
    mutationFn: () =>
      api.post<LedgerEntry>(`/customers/${customer.id}/ledger`, {
        entryType,
        amountCents,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: (entry) => {
      invalidate();
      onOpenChange(false);
      reset();
      toast.success(
        `${humanize(entry.entryType)} posted. Balance is now ${money(entry.runningBalanceCents)}.`
      );
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  const valid = amount !== null && amount > 0;
  const projected = customer.balance + amountCents;

  return (
    <>
      <Dialog
        open={open && !confirming}
        onOpenChange={(next) => {
          if (!next) reset();
          onOpenChange(next);
        }}
        title="Manual ledger entry"
        description="Corrects a balance by hand. Everything here is audited."
        width="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!valid}
              onClick={() => setConfirming(true)}
            >
              Review
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Entry type">
            <Select
              value={entryType}
              onChange={(e) => setEntryType(e.currentTarget.value as EntryType)}
            >
              <option value="adjustment">
                Adjustment — correct the balance either way
              </option>
              <option value="credit_memo">Credit memo — goodwill or return credit</option>
              <option value="payment">Payment — money received off-register</option>
            </Select>
          </Field>

          <Field
            label="Direction"
            hint={
              forcedCredit
                ? 'A payment or credit memo can only reduce the balance.'
                : 'Charge increases what the customer owes; credit reduces it.'
            }
          >
            <Select
              value={effectiveDirection}
              disabled={forcedCredit}
              onChange={(e) => setDirection(e.currentTarget.value as 'charge' | 'credit')}
            >
              <option value="charge">Charge the account (+)</option>
              <option value="credit">Credit the account (−)</option>
            </Select>
          </Field>

          <Field label="Amount" required hint="Must be more than zero.">
            <MoneyInput value={amount} onValueChange={setAmount} />
          </Field>
          <Field label="Reference">
            <Input
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
            />
          </Field>
          <Field label="Notes" hint="Say why. Future you will need it.">
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </Field>
          <div className="border-line bg-surface-sunken text-ink-muted rounded border p-2 font-mono text-xs tabular-nums">
            {money(customer.balance)} → {money(projected)}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && confirming}
        onOpenChange={(next) => {
          if (!next) setConfirming(false);
        }}
        title="Post this ledger entry?"
        description="It posts immediately and cannot be undone — only a second entry can correct it."
        confirmLabel="Post entry"
        loading={post.isPending}
        consequences={[
          `Posts ${amountCents > 0 ? '+' : ''}${money(amountCents)} as a ${humanize(entryType).toLowerCase()}`,
          `Balance ${money(customer.balance)} → ${money(projected)}`,
          notes.trim() ? `Note: ${notes.trim()}` : 'No note recorded',
        ]}
        onConfirm={() => post.mutate()}
      />
    </>
  );
}

/* ── Profile ──────────────────────────────────────────────────────────────── */

type ProfileDraft = {
  firstName: string;
  lastName: string;
  companyName: string;
  accountNumber: string;
  phone: string;
  email: string;
  notes: string;
  hasCredit: boolean;
  creditLimit: number | null;
  pctDiscount: string;
  taxExempt: boolean;
};

function toDraft(customer: Customer): ProfileDraft {
  return {
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    companyName: customer.companyName ?? '',
    accountNumber: customer.accountNumber ?? '',
    phone: customer.phone ?? '',
    email: customer.email ?? '',
    notes: customer.notes ?? '',
    hasCredit: !!customer.hasCredit,
    creditLimit: customer.creditLimit ?? 0,
    pctDiscount: String(customer.pctDiscount ?? 0),
    taxExempt: !!customer.taxExempt,
  };
}

function ProfilePanel({ customer }: { customer: Customer }) {
  const qc = useQueryClient();
  const initial = useMemo(() => toDraft(customer), [customer]);
  const [draft, setDraft] = useState<ProfileDraft>(initial);
  const set = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const discount = draft.pctDiscount.trim() === '' ? 0 : Number(draft.pctDiscount);
  const discountInvalid = !Number.isInteger(discount) || discount < 0 || discount > 100;

  // A sparse body: the API applies no defaults, so only changed keys are sent.
  const patch = useMemo(() => {
    const body: Record<string, unknown> = {};
    if (draft.firstName.trim() !== initial.firstName)
      body.firstName = draft.firstName.trim();
    if (draft.lastName.trim() !== initial.lastName) body.lastName = draft.lastName.trim();
    if (draft.companyName.trim() !== initial.companyName)
      body.companyName = draft.companyName.trim();
    // An account number can be changed but not blanked: the column is UNIQUE and
    // the API has no null form, so an empty string would collide across rows.
    if (
      draft.accountNumber.trim() &&
      draft.accountNumber.trim() !== initial.accountNumber
    )
      body.accountNumber = draft.accountNumber.trim();
    if (draft.phone.trim() !== initial.phone) body.phone = draft.phone.trim();
    if (draft.email.trim() !== initial.email) body.email = draft.email.trim();
    if (draft.notes !== initial.notes) body.notes = draft.notes;
    if (draft.hasCredit !== initial.hasCredit) body.hasCredit = draft.hasCredit;
    if ((draft.creditLimit ?? 0) !== (initial.creditLimit ?? 0))
      body.creditLimit = draft.creditLimit ?? 0;
    if (!discountInvalid && discount !== Number(initial.pctDiscount))
      body.pctDiscount = discount;
    if (draft.taxExempt !== initial.taxExempt) body.taxExempt = draft.taxExempt;
    return body;
  }, [draft, initial, discount, discountInvalid]);

  const dirty = Object.keys(patch).length > 0;

  const save = useMutation({
    mutationFn: () => api.put<Customer>(`/customers/${customer.id}`, patch),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: customerKey(customer.id) });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      setDraft(toDraft(updated));
      toast.success('Profile saved.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const moneyFieldsChanged = 'creditLimit' in patch || 'hasCredit' in patch;

  return (
    <Panel
      title="Profile"
      actions={
        dirty ? (
          <Button
            variant="primary"
            size="xs"
            icon={<Save className="size-3.5" />}
            loading={save.isPending}
            disabled={discountInvalid}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        ) : null
      }
      footer={
        dirty ? (
          <span>
            {Object.keys(patch).length} unsaved{' '}
            {Object.keys(patch).length === 1 ? 'change' : 'changes'}
            {moneyFieldsChanged ? ' · credit terms change what a cashier may charge' : ''}
          </span>
        ) : (
          <span>No unsaved changes</span>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <Input
              size="sm"
              value={draft.firstName}
              onChange={(e) => set('firstName', e.currentTarget.value)}
            />
          </Field>
          <Field label="Last name">
            <Input
              size="sm"
              value={draft.lastName}
              onChange={(e) => set('lastName', e.currentTarget.value)}
            />
          </Field>
        </div>
        <Field label="Company">
          <Input
            size="sm"
            value={draft.companyName}
            onChange={(e) => set('companyName', e.currentTarget.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Account number" hint="Cannot be blanked.">
            <Input
              size="sm"
              mono
              value={draft.accountNumber}
              onChange={(e) => set('accountNumber', e.currentTarget.value)}
            />
          </Field>
          <Field label="Phone" hint="Mirrors the primary contact.">
            <Input
              size="sm"
              mono
              value={draft.phone}
              onChange={(e) => set('phone', e.currentTarget.value)}
            />
          </Field>
        </div>
        <Field label="Email">
          <Input
            size="sm"
            type="email"
            value={draft.email}
            onChange={(e) => set('email', e.currentTarget.value)}
          />
        </Field>

        <div className="border-line-soft border-t pt-3">
          <Checkbox
            checked={draft.hasCredit}
            onCheckedChange={(checked) => set('hasCredit', checked)}
            label="House account enabled"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Credit limit"
            hint={draft.hasCredit ? 'Zero means no limit.' : undefined}
          >
            <MoneyInput
              size="sm"
              value={draft.creditLimit}
              onValueChange={(cents) => set('creditLimit', cents)}
              disabled={!draft.hasCredit}
            />
          </Field>
          <Field
            label="Discount %"
            error={discountInvalid ? 'Whole number, 0–100.' : null}
          >
            <Input
              size="sm"
              mono
              inputMode="numeric"
              invalid={discountInvalid}
              value={draft.pctDiscount}
              onChange={(e) => set('pctDiscount', e.currentTarget.value)}
            />
          </Field>
        </div>
        <Checkbox
          checked={draft.taxExempt}
          onCheckedChange={(checked) => set('taxExempt', checked)}
          label="Tax exempt"
        />
        <Field label="Notes">
          <Textarea
            rows={3}
            value={draft.notes}
            onChange={(e) => set('notes', e.currentTarget.value)}
          />
        </Field>
      </div>
    </Panel>
  );
}

/* ── PIN, card on file, wedged charge ─────────────────────────────────────── */

function SecurityPanel({ customer }: { customer: Customer }) {
  const qc = useQueryClient();
  const isManager = useAtLeast('manager');
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [clearPinOpen, setClearPinOpen] = useState(false);
  const [removeCardOpen, setRemoveCardOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: customerKey(customer.id) });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };

  const savePin = useMutation({
    mutationFn: (body: { pin: string } | { clear: true }) =>
      api.put<Customer>(`/customers/${customer.id}/pin`, body),
    onSuccess: (_updated, body) => {
      invalidate();
      setPinOpen(false);
      setClearPinOpen(false);
      setPin('');
      toast.success('clear' in body ? 'PIN cleared.' : 'PIN set.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const removeCard = useMutation({
    mutationFn: () => api.del<Customer>(`/customers/${customer.id}/card`),
    onSuccess: () => {
      invalidate();
      setRemoveCardOpen(false);
      toast.success('Saved card removed.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const pinValid = /^\d{4,6}$/.test(pin);

  return (
    <Panel
      title="Payment & security"
      actions={
        <Button
          variant="ghost"
          size="xs"
          icon={<KeyRound className="size-3.5" />}
          onClick={() => {
            setPin('');
            setPinOpen(true);
          }}
        >
          {customer.hasPin ? 'Change PIN' : 'Set PIN'}
        </Button>
      }
    >
      <DetailList columns={2}>
        <Detail label="Account PIN">
          {customer.hasPin ? (
            <Badge tone="positive">Set</Badge>
          ) : (
            <Badge tone="neutral">Not set</Badge>
          )}
        </Detail>
        <Detail label="Card on file" mono>
          {customer.hasCard
            ? `${customer.cardBrand ?? 'Card'} ••••${customer.cardLastFour ?? '????'}`
            : '—'}
        </Detail>
        <Detail label="Card expiry" mono>
          {customer.cardExp
            ? `${customer.cardExp.slice(0, 2)}/${customer.cardExp.slice(2)}`
            : '—'}
        </Detail>
        <Detail label="Card saved">
          {customer.cardSavedAt ? date(customer.cardSavedAt) : '—'}
        </Detail>
      </DetailList>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {customer.hasPin ? (
          <Button variant="danger" size="xs" onClick={() => setClearPinOpen(true)}>
            Clear PIN
          </Button>
        ) : null}
        {customer.hasCard ? (
          <Button
            variant="danger"
            size="xs"
            icon={<CreditCard className="size-3.5" />}
            onClick={() => setRemoveCardOpen(true)}
          >
            Remove card
          </Button>
        ) : null}
        {isManager ? (
          <Button variant="ghost" size="xs" onClick={() => setResolveOpen(true)}>
            Resolve a stuck card charge…
          </Button>
        ) : null}
      </div>
      <p className="text-2xs text-ink-faint mt-2 leading-relaxed">
        The card number itself is never stored here or sent to this console — only the
        brand, last four and expiry.
      </p>

      <Dialog
        open={pinOpen}
        onOpenChange={setPinOpen}
        title={customer.hasPin ? 'Change account PIN' : 'Set account PIN'}
        description="The customer types this at the register to authorise a house-account charge."
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPinOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!pinValid}
              loading={savePin.isPending}
              onClick={() => savePin.mutate({ pin })}
            >
              Save PIN
            </Button>
          </>
        }
      >
        <Field
          label="PIN"
          hint="4 to 6 digits."
          error={pin && !pinValid ? 'Digits only, 4–6 of them.' : null}
        >
          <Input
            autoFocus
            mono
            inputMode="numeric"
            maxLength={6}
            value={pin}
            invalid={!!pin && !pinValid}
            onChange={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))}
          />
        </Field>
      </Dialog>

      <ConfirmDialog
        open={clearPinOpen}
        onOpenChange={setClearPinOpen}
        title="Clear this account PIN?"
        description="Charging on account will no longer ask for a PIN at the register."
        confirmLabel="Clear PIN"
        loading={savePin.isPending}
        consequences={['Removes PIN verification from house-account charges']}
        onConfirm={() => savePin.mutate({ clear: true })}
      />

      <ConfirmDialog
        open={removeCardOpen}
        onOpenChange={setRemoveCardOpen}
        title="Remove the saved card?"
        description="The stored token is deleted. The customer must present the card again to save a new one."
        confirmLabel="Remove card"
        loading={removeCard.isPending}
        consequences={[
          `Removes ${customer.cardBrand ?? 'the card'} ••••${customer.cardLastFour ?? '????'}`,
          'Any card-on-file order settlement will fail until a new card is saved',
        ]}
        onConfirm={() => removeCard.mutate()}
      />

      {isManager ? (
        <ResolveChargeDialog
          customerId={customer.id}
          open={resolveOpen}
          onOpenChange={setResolveOpen}
        />
      ) : null}
    </Panel>
  );
}

/**
 * Manager attestation for a saved-card charge that never reached a clean
 * outcome. The API exposes no way to LIST a customer's stuck attempts (see the
 * note in the dialog), so the attempt number comes from the register error or
 * the support ticket that sent the manager here.
 */
function ResolveChargeDialog({
  customerId,
  open,
  onOpenChange,
}: {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [attemptId, setAttemptId] = useState('');
  const [resolution, setResolution] = useState<
    'verified_no_capture' | 'verified_compensated'
  >('verified_no_capture');
  const [reference, setReference] = useState('');
  const [confirming, setConfirming] = useState(false);

  const resolve = useMutation({
    mutationFn: () =>
      api.post<{ success: boolean; attemptId: number; resolution: string }>(
        `/customers/${customerId}/card-charge-attempts/${attemptId}/resolve`,
        { resolution, reference: reference.trim() }
      ),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: customerKey(customerId) });
      onOpenChange(false);
      setConfirming(false);
      setAttemptId('');
      setReference('');
      toast.success(
        `Attempt #${result.attemptId} resolved as ${humanize(result.resolution)}.`
      );
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  const valid = /^\d+$/.test(attemptId.trim()) && reference.trim().length > 0;

  return (
    <>
      <Dialog
        open={open && !confirming}
        onOpenChange={onOpenChange}
        title="Resolve a stuck saved-card charge"
        description="Records that a human checked the gateway. Use it only after looking the attempt up there."
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!valid}
              onClick={() => setConfirming(true)}
            >
              Review
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field
            label="Attempt number"
            required
            hint="From the register's refusal message or the support ticket — the API has no list of stuck attempts yet."
          >
            <Input
              autoFocus
              mono
              inputMode="numeric"
              value={attemptId}
              onChange={(e) => setAttemptId(e.currentTarget.value.replace(/\D/g, ''))}
            />
          </Field>
          <Field label="What did the gateway show?" required>
            <Select
              value={resolution}
              onChange={(e) =>
                setResolution(
                  e.currentTarget.value as 'verified_no_capture' | 'verified_compensated'
                )
              }
            >
              <option value="verified_no_capture">No capture — no money moved</option>
              <option value="verified_compensated">
                Captured, then refunded or voided
              </option>
            </Select>
          </Field>
          <Field
            label="Gateway reference"
            required
            hint="The reference you inspected or the refund id you issued."
          >
            <Input
              mono
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
            />
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && confirming}
        onOpenChange={(next) => {
          if (!next) setConfirming(false);
        }}
        title="Record this attestation?"
        description="It permanently closes the attempt. Neither sync nor a retry can post it afterwards."
        confirmLabel="Record attestation"
        typeToConfirm="RESOLVE"
        loading={resolve.isPending}
        consequences={[
          `Closes attempt #${attemptId} as ${humanize(resolution).toLowerCase()}`,
          resolution === 'verified_no_capture'
            ? 'Asserts that NO money moved — the server refuses this for an approved capture'
            : 'Asserts the capture was already reversed at the gateway',
          `Gateway reference: ${reference.trim()}`,
        ]}
        onConfirm={() => resolve.mutate()}
      />
    </>
  );
}

/* ── Contacts (manager) ───────────────────────────────────────────────────── */

const CONTACT_TYPES: CustomerContactType[] = ['mobile', 'home', 'work', 'fax', 'other'];

function ContactsPanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const isManager = useAtLeast('manager');
  const [editing, setEditing] = useState<CustomerContact | 'new' | null>(null);
  const [removing, setRemoving] = useState<CustomerContact | null>(null);

  const contacts = useQuery({
    queryKey: customerKey(customerId, 'contacts'),
    queryFn: () => api.get<CustomerContact[]>(`/customers/${customerId}/contacts`),
    enabled: isManager,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: customerKey(customerId) });
    void qc.invalidateQueries({ queryKey: ['customers'] });
  };

  const remove = useMutation({
    mutationFn: (contact: CustomerContact) =>
      api.del(`/customers/${customerId}/contacts/${contact.id}`),
    onSuccess: () => {
      invalidate();
      setRemoving(null);
      toast.success('Contact removed.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  // A primary flip has its own endpoint — PATCH refuses it.
  const setPrimary = useMutation({
    mutationFn: (contact: CustomerContact) =>
      api.post(`/customers/${customerId}/contacts/${contact.id}/set-primary`),
    onSuccess: () => {
      invalidate();
      toast.success('Primary contact updated — the customer phone now mirrors it.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Panel
      title="Contacts"
      actions={
        isManager ? (
          <Button
            variant="ghost"
            size="xs"
            icon={<Plus className="size-3.5" />}
            onClick={() => setEditing('new')}
          >
            Add
          </Button>
        ) : null
      }
      bleed
    >
      <ManagerGate isManager={isManager} query={contacts} what="contacts">
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState compact title="No contacts" />
          ) : (
            <div className="divide-line-soft divide-y">
              {rows.map((contact) => (
                <div key={contact.id} className="flex items-center gap-2 px-3 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-ink truncate font-mono text-xs tabular-nums">
                        {phoneFmt(contact.value)}
                      </span>
                      {contact.isPrimary ? <Badge tone="signal">Primary</Badge> : null}
                    </div>
                    <div className="text-2xs text-ink-faint truncate">
                      {humanize(contact.contactType ?? 'other')}
                      {contact.label ? ` · ${contact.label}` : ''}
                    </div>
                  </div>
                  {/* A fax can never be primary — the server refuses it, so the
                      button is absent rather than dead. */}
                  {!contact.isPrimary && contact.contactType !== 'fax' ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Make primary"
                      onClick={() => setPrimary.mutate(contact)}
                    >
                      <Star className="size-3.5" />
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Edit"
                    onClick={() => setEditing(contact)}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Remove"
                    onClick={() => setRemoving(contact)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )
        }
      </ManagerGate>

      {editing ? (
        <ContactDialog
          customerId={customerId}
          contact={editing === 'new' ? null : editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this contact?"
        description="It disappears from the customer record."
        confirmLabel="Remove"
        loading={remove.isPending}
        consequences={removing ? [`Removes ${phoneFmt(removing.value)}`] : []}
        onConfirm={() => removing && remove.mutate(removing)}
      />
    </Panel>
  );
}

function ContactDialog({
  customerId,
  contact,
  open,
  onOpenChange,
}: {
  customerId: string;
  contact: CustomerContact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(contact?.value ?? '');
  const [contactType, setContactType] = useState<CustomerContactType>(
    contact?.contactType ?? 'mobile'
  );
  const [label, setLabel] = useState(contact?.label ?? '');
  const [primary, setPrimary] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      contact
        ? api.patch<CustomerContact>(`/customers/${customerId}/contacts/${contact.id}`, {
            value: value.trim(),
            contactType,
            label: label.trim() || null,
          })
        : api.post<CustomerContact>(`/customers/${customerId}/contacts`, {
            value: value.trim(),
            contactType,
            label: label.trim() || null,
            ...(primary && contactType !== 'fax' ? { isPrimary: true } : {}),
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerKey(customerId) });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
      toast.success(contact ? 'Contact updated.' : 'Contact added.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={contact ? 'Edit contact' : 'Add contact'}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!value.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Number or address" required>
          <Input
            autoFocus
            mono
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            maxLength={64}
          />
        </Field>
        <Field label="Type">
          <Select
            value={contactType}
            onChange={(e) => setContactType(e.currentTarget.value as CustomerContactType)}
          >
            {CONTACT_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanize(type)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Label" hint="Optional — “warehouse”, “after hours”…">
          <Input
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            maxLength={32}
          />
        </Field>
        {!contact && contactType !== 'fax' ? (
          <Checkbox
            checked={primary}
            onCheckedChange={setPrimary}
            label="Make this the primary number (mirrors to the customer phone)"
          />
        ) : null}
        {contact ? (
          <p className="text-2xs text-ink-faint">
            Use the star in the list to change which contact is primary — the edit form
            cannot flip it.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ── Addresses (manager) ──────────────────────────────────────────────────── */

const ADDRESS_TYPES: CustomerAddressType[] = ['home', 'business', 'delivery', 'other'];

function AddressesPanel({ customerId }: { customerId: string }) {
  const qc = useQueryClient();
  const isManager = useAtLeast('manager');
  const [editing, setEditing] = useState<CustomerAddress | 'new' | null>(null);
  const [removing, setRemoving] = useState<CustomerAddress | null>(null);

  const addresses = useQuery({
    queryKey: customerKey(customerId, 'addresses'),
    queryFn: () => api.get<CustomerAddress[]>(`/customers/${customerId}/addresses`),
    enabled: isManager,
  });

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: customerKey(customerId, 'addresses') });

  const remove = useMutation({
    mutationFn: (address: CustomerAddress) =>
      api.del(`/customers/${customerId}/addresses/${address.id}`),
    onSuccess: () => {
      invalidate();
      setRemoving(null);
      toast.success('Address removed.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  // The default flag has its own endpoint; PATCH answers 400 use_set_default.
  const setDefault = useMutation({
    mutationFn: (address: CustomerAddress) =>
      api.post(`/customers/${customerId}/addresses/${address.id}/set-default`),
    onSuccess: () => {
      invalidate();
      toast.success('Default address updated.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Panel
      title="Addresses"
      actions={
        isManager ? (
          <Button
            variant="ghost"
            size="xs"
            icon={<Plus className="size-3.5" />}
            onClick={() => setEditing('new')}
          >
            Add
          </Button>
        ) : null
      }
      bleed
    >
      <ManagerGate isManager={isManager} query={addresses} what="addresses">
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState compact title="No addresses" />
          ) : (
            <div className="divide-line-soft divide-y">
              {rows.map((address) => (
                <div key={address.id} className="flex items-start gap-2 px-3 py-1.5">
                  <MapPin className="text-ink-faint mt-0.5 size-3.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-ink truncate text-xs">
                        {[address.line1, address.line2].filter(Boolean).join(', ') || '—'}
                      </span>
                      {address.isDefault ? <Badge tone="signal">Default</Badge> : null}
                    </div>
                    <div className="text-2xs text-ink-faint truncate">
                      {[address.city, address.state, address.zip]
                        .filter(Boolean)
                        .join(' ')}
                      {address.addressType ? ` · ${humanize(address.addressType)}` : ''}
                    </div>
                  </div>
                  {!address.isDefault ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Make default"
                      onClick={() => setDefault.mutate(address)}
                    >
                      <Star className="size-3.5" />
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Edit"
                    onClick={() => setEditing(address)}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title="Remove"
                    onClick={() => setRemoving(address)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )
        }
      </ManagerGate>

      {editing ? (
        <AddressDialog
          customerId={customerId}
          address={editing === 'new' ? null : editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this address?"
        confirmLabel="Remove"
        loading={remove.isPending}
        consequences={removing ? [`Removes ${removing.line1 ?? 'this address'}`] : []}
        onConfirm={() => removing && remove.mutate(removing)}
      />
    </Panel>
  );
}

function AddressDialog({
  customerId,
  address,
  open,
  onOpenChange,
}: {
  customerId: string;
  address: CustomerAddress | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [line1, setLine1] = useState(address?.line1 ?? '');
  const [line2, setLine2] = useState(address?.line2 ?? '');
  const [city, setCity] = useState(address?.city ?? '');
  const [state, setState] = useState(address?.state ?? '');
  const [zip, setZip] = useState(address?.zip ?? '');
  const [addressType, setAddressType] = useState<CustomerAddressType>(
    address?.addressType ?? 'delivery'
  );
  const [notes, setNotes] = useState(address?.notes ?? '');
  const [isDefault, setIsDefault] = useState(false);

  const body = {
    addressType,
    line1: line1.trim() || null,
    line2: line2.trim() || null,
    city: city.trim() || null,
    state: state.trim() || null,
    zip: zip.trim() || null,
    notes: notes.trim() || null,
  };

  const save = useMutation({
    mutationFn: () =>
      address
        ? // isDefault is deliberately NOT sent: the API answers 400
          // `use_set_default` — the star in the list is the way.
          api.patch<CustomerAddress>(
            `/customers/${customerId}/addresses/${address.id}`,
            body
          )
        : api.post<CustomerAddress>(`/customers/${customerId}/addresses`, {
            ...body,
            ...(isDefault ? { isDefault: true } : {}),
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: customerKey(customerId, 'addresses') });
      onOpenChange(false);
      toast.success(address ? 'Address updated.' : 'Address added.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const empty = !line1.trim() && !city.trim() && !zip.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={address ? 'Edit address' : 'Add address'}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={empty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Type">
          <Select
            value={addressType}
            onChange={(e) => setAddressType(e.currentTarget.value as CustomerAddressType)}
          >
            {ADDRESS_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanize(type)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Address line 1" required>
          <Input
            autoFocus
            value={line1}
            onChange={(e) => setLine1(e.currentTarget.value)}
          />
        </Field>
        <Field label="Address line 2">
          <Input value={line2} onChange={(e) => setLine2(e.currentTarget.value)} />
        </Field>
        <div className="grid grid-cols-[minmax(0,1fr)_4rem_6rem] gap-2">
          <Field label="City">
            <Input value={city} onChange={(e) => setCity(e.currentTarget.value)} />
          </Field>
          <Field label="State">
            <Input
              mono
              maxLength={8}
              value={state}
              onChange={(e) => setState(e.currentTarget.value)}
            />
          </Field>
          <Field label="ZIP">
            <Input
              mono
              maxLength={16}
              value={zip}
              onChange={(e) => setZip(e.currentTarget.value)}
            />
          </Field>
        </div>
        <Field label="Notes" hint="Gate codes, dock instructions, where to leave it.">
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
        </Field>
        {!address ? (
          <Checkbox
            checked={isDefault}
            onCheckedChange={setIsDefault}
            label="Make this the default address"
          />
        ) : (
          <p className="text-2xs text-ink-faint">
            Use the star in the list to change the default — the edit form cannot flip it.
          </p>
        )}
      </div>
    </Dialog>
  );
}

/* ── Manager gate ─────────────────────────────────────────────────────────────
   Contacts and addresses need `customers.contacts.*` (manager). Anyone lower
   gets an explanation, not a crash — and if the server disagrees with our local
   role check, the 403 renders the same way.                                  */

function ManagerGate<T>({
  isManager,
  query,
  what,
  children,
}: {
  isManager: boolean;
  query: {
    data: T[] | undefined;
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
  };
  what: string;
  children: (rows: T[]) => ReactNode;
}) {
  const forbidden = query.error instanceof ApiError && query.error.isForbidden;

  if (!isManager || forbidden) {
    return (
      <div className="px-3 py-4 text-center">
        <div className="text-ink-muted text-xs">Requires manager</div>
        <div className="text-2xs text-ink-faint mt-0.5">
          Customer {what} are restricted to manager and admin accounts.
        </div>
      </div>
    );
  }

  if (query.error) {
    return (
      <ErrorState
        title={`Could not load ${what}`}
        message={errorMessage(query.error)}
        onRetry={() => query.refetch()}
        className="m-3"
      />
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="flex flex-col gap-1.5 p-3">
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    );
  }

  return <>{children(query.data)}</>;
}
